import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  MenuItem,
  ModalProps,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { DateTimePicker } from '@mui/x-date-pickers';
import { addDays, differenceInCalendarDays, format, isToday } from 'date-fns';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Bell01, CalendarCheck01, CheckVerified01, SearchMD } from 'untitledui-js-base';
import { UserResponse } from '../../api';
import { getValidDate } from '../../utils/parseDate';
import { BasicDialog } from '../common/BasicDialog';
import { CustomDay } from '../common/CustomDay';
import { Role } from '../types';
import {
  AvailableRentalCar,
  AvailableRentalCars,
  CarBranch,
  CarRentalRequest,
  RENT_SOURCES,
  RentCarRequest
} from '../types/rental';

interface AvailableCarItemProps {
  car: AvailableRentalCar;
  branches: CarBranch[];
  onCarChoose: (forceApproval?: boolean) => void;
  returnDateSelected?: boolean;
  canBlock?: boolean;
}

const AvailableCarItem = ({ car, branches, onCarChoose, returnDateSelected, canBlock }: AvailableCarItemProps) => (
  <Grid container columnSpacing={2} rowSpacing={1} alignItems="center">
    <Grid size={{ xs: 12, md: 6, lg: 2 }}>
      <Stack gap={1}>
        <Typography fontSize="14px" fontWeight="700">
          {[[car.brand, car.model].filter(Boolean).join(' '), car.color, car.manufacturedYear, car.plateNumber]
            .filter(Boolean)
            .join(', ')}
        </Typography>
      </Stack>
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 2 }}>
      <Stack>
        <Typography fontSize="14px" component="span">
          Aktuální pobočka
        </Typography>
        <Typography fontSize="14px" fontWeight="700">
          {branches?.find((branch) => branch.id === car.branchId)?.title}
        </Typography>
      </Stack>
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 2 }}>
      <Stack>
        <Typography fontSize="14px" component="span">
          Základní denní nájemné
        </Typography>
        <Typography fontSize="14px" fontWeight="700">
          {`${car.dailyRate} Kč `}
          <Typography component="span" fontSize="14px" fontWeight={400}>
            (bez DPH)
          </Typography>
        </Typography>
      </Stack>
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
      {!isEmpty(car.rentalInConflict) && !Boolean(car.rentalInConflict.isReturned) && (
        <Box>
          <Typography color="error" fontWeight="700" component="span" fontSize="0.6rem">
            {car.rentalInConflict.arrangeUserFullName}
          </Typography>
          <Stack
            sx={{
              border: '1px solid',
              borderColor: 'red',
              borderRadius: 2,
              paddingX: '15px',
              paddingY: '8px'
            }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <Bell01 color="red" size="1.2rem" />
              <Stack>
                <Typography fontWeight="700" fontSize="12px" color="error">
                  Naplánovaná blokace
                </Typography>
                {getValidDate(car.rentalInConflict.reservationDate) && (
                  <Typography fontWeight="700" fontSize="12px">
                    {`Do: ${format(getValidDate(car.rentalInConflict.reservationDate)!, 'dd.MM.yyyy HH:mm')}`}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}
    </Grid>
    <Grid size={{ xs: 12, md: 6, lg: 3 }} textAlign={{ lg: 'end' }} pr={2}>
      {(isEmpty(car.rentalInConflict) || Boolean(car.rentalInConflict.isReturned)) && (
        <Stack direction="row" gap={1} justifyContent={{ md: 'end' }}>
          {isToday(new Date()) && returnDateSelected && (
            <Button variant="contained" size="small" onClick={() => onCarChoose(true)} color="success">
              Zapůjčit
            </Button>
          )}
          {canBlock && (
            <Button variant="contained" size="small" onClick={() => onCarChoose(false)} color="warning">
              Blokovat
            </Button>
          )}
        </Stack>
      )}
    </Grid>
  </Grid>
);

interface RentalCarSelectionModalProps extends Omit<ModalProps, 'children'> {
  open: boolean;
  close: () => void;
  availableCars?: AvailableRentalCars;
  onCarSearch?: (formData: CarRentalRequest) => void;
  isSearching?: boolean;
  onCarRent?: (
    rentData: RentCarRequest,
    callbacks?: {
      onSuccess?: () => void;
      onError?: (error: any) => void;
    }
  ) => void;
  userId?: number;
  accidentId?: number;
  isProcessingRent?: boolean;
  userList?: UserResponse[];
  isLoadingUserList?: boolean;
  fetchUserList?: () => void;
}

export const RentalCarSelectionModal = ({
  close,
  open,
  availableCars,
  onCarSearch,
  isSearching,
  onCarRent,
  userId,
  accidentId,
  isProcessingRent,
  userList,
  isLoadingUserList,
  fetchUserList
}: RentalCarSelectionModalProps) => {
  const [initialRentalDate] = useState(() => new Date());
  const [initialReturnDate] = useState(() => addDays(new Date(), 7));
  const [chosenCar, setChosenCar] = useState<AvailableRentalCar | null>(null);
  const [chosenDailyRate, setChosenDailyRate] = useState<string>('');
  const [chosenUserId, setChosenUserId] = useState<number | undefined>(undefined);
  const [openAutocomplete, setOpenAutocomplete] = useState(false);
  const [handleDeliveryDate, setHandleDeliveryDate] = useState(false);
  const [openReservationDialog, setOpenReservationDialog] = useState(false);
  const [forceApproval, setForceApproval] = useState<boolean>(true);

  const hasSetDefaultReturnDateRef = useRef(false);

  const methods = useForm<CarRentalRequest>({
    shouldUnregister: true,
    defaultValues: {
      rentalDate: initialRentalDate,
      returnDate: initialReturnDate,
      searchPhrase: '',
      branchId: 0,
      carParameters: []
    }
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue
  } = methods;

  const rentalDate = watch('rentalDate') ?? new Date();
  const returnDate = watch('returnDate');
  const rentalDuration = useMemo(() => {
    if (rentalDate && returnDate) {
      return differenceInCalendarDays(returnDate, rentalDate) + 1;
    }
    return 7;
  }, [rentalDate, returnDate]);

  useEffect(() => {
    if (handleDeliveryDate && !hasSetDefaultReturnDateRef.current) {
      const expectedReturnDate = addDays(rentalDate, 7);
      setValue('returnDate', expectedReturnDate, { shouldValidate: true });
      hasSetDefaultReturnDateRef.current = true;
    }

    if (!handleDeliveryDate) {
      hasSetDefaultReturnDateRef.current = false;
    }
  }, [handleDeliveryDate, rentalDate, setValue]);

  const handleRentCar = () => {
    const objectId = accidentId ?? chosenUserId;
    const sourceType = accidentId ? RENT_SOURCES.ACCIDENT : RENT_SOURCES.USER;

    const rentPayload: RentCarRequest = {
      rental: {
        arrange: {
          type: Role.Dealer,
          userId: userId as number
        },
        carId: chosenCar?.id as number,
        rentalDate: rentalDate ?? new Date(),
        forceApproval: forceApproval,
        ...(Boolean(chosenDailyRate) && {
          dailyRate: chosenDailyRate
        }),
        ...(handleDeliveryDate && {
          returnDate: returnDate ?? addDays(rentalDate, 1)
        })
      },
      source: {
        type: sourceType,
        objectId
      }
    };

    onCarRent?.(rentPayload, {
      onSuccess: () => {
        setChosenCar(null);
        setHandleDeliveryDate(false);
        setOpenReservationDialog(false);
        setForceApproval(false);
      }
    });
  };

  const handleOpen = () => {
    setOpenAutocomplete(true);
    fetchUserList?.();
  };

  const handleClose = () => {
    setOpenAutocomplete(false);
  };

  return (
    <BasicDialog open={open} onClose={close} title="Nová zápůjčka" maxWidth="lg" fullWidth>
      <>
        <Box>
          <form onSubmit={handleSubmit((data) => onCarSearch?.(data))}>
            <Grid container spacing={2} pt={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  control={control}
                  name="rentalDate"
                  render={({ field: { onChange, value } }) => (
                    <DateTimePicker
                      label="Datum zapůjčení"
                      value={value}
                      onChange={onChange}
                      slots={{
                        day: CustomDay
                      }}
                      slotProps={{
                        textField: {
                          error: Boolean(errors.rentalDate),
                          helperText: errors.rentalDate?.message,
                          fullWidth: true
                        }
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={handleDeliveryDate} onChange={(e) => setHandleDeliveryDate(e.target.checked)} />
                  }
                  label="Zadat datum vrácení"
                />
              </Grid>
              {handleDeliveryDate && (
                <>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                      control={control}
                      name="returnDate"
                      render={({ field: { onChange, value } }) => (
                        <DateTimePicker
                          label="Datum vrácení"
                          value={value}
                          slots={{
                            day: CustomDay
                          }}
                          onChange={(val) => {
                            if (val && val <= rentalDate) {
                              onChange(addDays(rentalDate, 1));
                            } else {
                              onChange(val);
                            }
                          }}
                          minDateTime={rentalDate}
                          slotProps={{
                            textField: {
                              error: Boolean(errors.returnDate),
                              helperText: errors.returnDate?.message,
                              fullWidth: true
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Celková doba zápůjčky"
                      variant="outlined"
                      value={rentalDuration}
                      disabled
                      fullWidth
                    />
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12, md: 8 }}>
                <Controller
                  control={control}
                  name="searchPhrase"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      label="Vyhledávání"
                      placeholder="vin, značka, model, barva, ..."
                      variant="outlined"
                      onChange={onChange}
                      value={value}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="branch-label"
                    sx={{
                      backgroundColor: 'white',
                      px: 0.5
                    }}
                  >
                    Pobočka
                  </InputLabel>
                  <Controller
                    control={control}
                    name="branchId"
                    render={({ field: { onChange, value } }) => (
                      <Select value={value} onChange={onChange} labelId="branch-label" displayEmpty>
                        <MenuItem value={0}>Nevybráno</MenuItem>
                        {availableCars?.branches
                          ?.filter((branch) => Boolean(branch.visibility))
                          .map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.title}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid size={12}>
                <Controller
                  control={control}
                  name="carParameters"
                  render={({ field: { onChange, value = [] } }) => {
                    const handleCheckboxChange =
                      (optionName: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
                        if (event.target.checked) {
                          onChange([...value, optionName]);
                        } else {
                          onChange(value.filter((v) => v !== optionName));
                        }
                      };

                    return (
                      <FormGroup row>
                        {availableCars?.carParameters?.map((option) => (
                          <FormControlLabel
                            key={option.name}
                            control={
                              <Checkbox
                                checked={value.includes(option.name)}
                                onChange={handleCheckboxChange(option.name)}
                                size="small"
                              />
                            }
                            label={<Typography variant="body2">{option.label}</Typography>}
                          />
                        ))}
                      </FormGroup>
                    );
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Stack direction={{ xs: 'column-reverse', md: 'row' }} justifyContent="space-between" gap={2}>
                  <Button
                    type="submit"
                    variant="outlined"
                    color="primary"
                    startIcon={<SearchMD />}
                    loading={isSearching}
                  >
                    Vyhledat dostupná vozidla
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<CalendarCheck01 />}
                    loading={isSearching}
                    onClick={() => setOpenReservationDialog(true)}
                  >
                    Blokace jakéhokoliv auta
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Box>
        <Divider sx={{ my: 4 }} />
        <Stack spacing={2}>
          {!isEmpty(availableCars?.cars) ? (
            availableCars?.cars?.map((car) => (
              <Stack spacing={1} key={car.id}>
                <AvailableCarItem
                  car={car}
                  branches={availableCars?.branches}
                  onCarChoose={(forceApproval) => {
                    setChosenDailyRate(car.dailyRate);
                    setChosenCar(car);
                    setForceApproval(forceApproval ?? false);
                  }}
                  returnDateSelected={!!returnDate}
                  canBlock={availableCars?.isBlock}
                />
                <Divider />
              </Stack>
            ))
          ) : (
            <Typography textAlign="center" variant="body2">
              Nenalezli jsme žádné auta. Zkuste upravit své hledání nebo vytvořte rezervaci bez výběru auta...
            </Typography>
          )}
        </Stack>

        {/* Car selection dialog */}
        <Dialog onClose={() => setChosenCar(null)} open={!!chosenCar}>
          <DialogTitle>Potvrdit výběr</DialogTitle>
          <DialogContent>
            {handleDeliveryDate && returnDate && (
              <>
                <Typography>
                  Opravdu chcete {availableCars?.isBlock ? 'zablokovat' : 'zapůjčit'} toto vozidlo v termínu:
                </Typography>
                <Typography fontWeight="700">
                  {`${format(rentalDate, 'dd.MM.yyyy')} - ${format(returnDate, 'dd.MM.yyyy')} (${rentalDuration} dnů)`}
                </Typography>
              </>
            )}
            {!handleDeliveryDate && (
              <Typography>
                Opravdu chcete {availableCars?.isBlock ? 'zablokovat' : 'zapůjčit'} toto vozidlo od:{' '}
                <strong>{format(rentalDate, 'dd.MM.yyyy')}</strong>
              </Typography>
            )}
            {/* If not accidentId, we will create rent without accident */}
            {!accidentId && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <Autocomplete
                    open={openAutocomplete}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.fullName as string}
                    options={userList ?? []}
                    loading={isLoadingUserList}
                    onChange={(_, value) => setChosenUserId(value?.id)}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.fullName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vyberte zákazníka..."
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingUserList && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}

            <Typography mt={2} mb={1} fontWeight="700">
              Denní nájemné{' '}
              <Typography component="span" fontWeight="400" fontSize="14px">
                (bez DPH)
              </Typography>
            </Typography>
            <TextField
              value={chosenDailyRate}
              onChange={(event) => setChosenDailyRate(event.target.value)}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">Kč</InputAdornment>
                }
              }}
            />
            <Stack mt={2} textAlign="center" gap={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckVerified01 />}
                color="success"
                onClick={handleRentCar}
                loading={isProcessingRent}
              >
                Vytvořit {forceApproval ? 'zápůjčku' : availableCars?.isBlock ? 'blokaci' : 'zápůjčku'}
              </Button>
              <Button variant="text" onClick={() => setChosenCar(null)}>
                Zavřít
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {/* Car selection dialog */}
        <Dialog onClose={() => setOpenReservationDialog(false)} open={openReservationDialog}>
          <DialogTitle>Potvrdit rezervaci</DialogTitle>
          <DialogContent>
            {handleDeliveryDate && returnDate && (
              <>
                <Typography>Opravdu chcete vytvořit rezervaci na vozidlo v termínu:</Typography>
                <Typography fontWeight="700">
                  {`${format(rentalDate, 'dd.MM.yyyy HH:mm')} - ${format(returnDate, 'dd.MM.yyyy HH:mm')} (${rentalDuration} dnů)`}
                </Typography>
              </>
            )}
            {!handleDeliveryDate && (
              <Typography>
                Opravdu chcete vytvořit rezervaci na vozidlo od:{' '}
                <strong>{format(rentalDate, 'dd.MM.yyyy HH:mm')}</strong>
              </Typography>
            )}
            {/* If not accidentId, we will create rent without accident */}
            {!accidentId && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <Autocomplete
                    open={openAutocomplete}
                    onOpen={handleOpen}
                    onClose={handleClose}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.fullName as string}
                    options={userList ?? []}
                    loading={isLoadingUserList}
                    onChange={(_, value) => setChosenUserId(value?.id)}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.fullName}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vyberte zákazníka..."
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {isLoadingUserList && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            )}
            <Stack mt={2} textAlign="center" gap={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CalendarCheck01 />}
                color="success"
                onClick={handleRentCar}
                loading={isProcessingRent}
              >
                {forceApproval && !availableCars?.isBlock ? 'Zapůjčit vozidlo' : 'Vytvořit blokaci'}
              </Button>
              <Button variant="text" onClick={() => setOpenReservationDialog(false)}>
                Zavřít
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    </BasicDialog>
  );
};
